import Vue from 'vue';

let handlers = [];
export function closeDownKeyboard(tableElement) {
	handlers.forEach((h)=>{
		tableElement.removeEventListener(h.eventName, h.fn);
	});
	handlers = [];
}

export default async function setupKeyboard(tableElement) {
	// Reusable addEventListener function with selector options
	// This gets attached via `this.$el` when src\components\Table.vue is mounted
	const addHandler = (selector, eventName, handler) => {
		if (typeof eventName === 'undefined') {
			eventName = selector;
			selector = undefined;
		}

		let tempHandler = null;
		tableElement.addEventListener(eventName, tempHandler = (e) => {
			let currentTarget = e.currentTarget;
			if (selector) {
				currentTarget = e.target.matches(selector);
				if (!currentTarget) {
					return;
				}
			}
			return handler.call(currentTarget, e);
		});

		handlers.push({ eventName, 'fn': tempHandler });

		return tableElement;
	};

	// Set up query selectors for reusability
	const focusableElements = ['.cog-lookup--loading', 'input[type=text]', 'input[type=date]', 'input[type=time]', 'input[type=email]', 'input[type=tel]', 'input[type=url]', 'input[type=password]', 'select', 'textarea'];

	// Filter out the inputs from the table fields that are not focusable
	const getFocusableInputs = container => {
		const element = container
			.map(container => container.querySelector(focusableElements))
			.filter(el => el)
			.filter(el => !el.focusableElements);
		return element;
	};

	const isTable = $this => {
		return !!$this.closest('tbody');
	};

	/* Adding new table row/deleting automatically added table row
		Add new table row when enter/tab key pressed in last editable table cell
		Delete new table row when tabbing out of the first field on a row just added via tabbing
	*/
	addHandler(focusableElements, 'keydown', (e) => {
		const $this = e.target;
		if (!isTable($this))
			return;
		const key = (e.key || e.which || 0);
		const isTextarea = $this.matches('textarea');

		// Adding newline to textarea takes precedence over adding table row
		if ((key === 'Tab' && !e.shiftKey) || (key === 'Enter' && !isTextarea)) {
			const row = $this.closest('tr');
			const cells = Array.from(row.querySelectorAll('td'));

			// Check if it is the current last table in the row
			if ([...row.parentElement.children].indexOf(row) === [...row.parentElement.children].length - 1) {
				const rowInputs = getFocusableInputs(cells);
				const table = $this.closest('.cog-table');

				if (row.classList.contains('cog-auto-row') && rowInputs[0] === $this && (!$this.value || !$this.value.length)) {
					// Delete new row when tabbing out of the first cell in the new row of a table and that cell is blank, and move focus to the Add Row button
					e.preventDefault();

					row.querySelector('.cog-table__remove').click();
					table.querySelector('.cog-table__button-container button.cog-table__add').focus();
				}
				else if (rowInputs.slice(-1)[0] === $this) {
					// Tabbing or pressing Enter in the last field on the last row adds a new row and sets focus on the first field
					e.preventDefault();

					// Click the add row button
					table.querySelector('.cog-table__button-container button.cog-table__add').click();

					Vue.nextTick(() => {
						// Set focus on the first editable cell in the new row
						const tableBody = $this.closest('.cog-table tbody');
						const lastTableRow = tableBody.querySelector('tr:last-child');
						lastTableRow.classList.add('cog-auto-row');
						const newRowInputsArray = Array.from(lastTableRow.querySelectorAll('td'));
						$this.blur();

						const inputs = getFocusableInputs(newRowInputsArray);
						inputs[0].focus();
					});
				}

				row.classList.remove('cog-auto-row');
			}
		}
	});

	/* Home/End key behavior
		Home = first editable cell of active row
		CTRL + Home = first editable cell in first row, first column
		End = last editable cell of active row
		CTRL + End = last editable cell in last row, last column
	*/
	addHandler(focusableElements, 'keydown', (e) => {
		const $this = e.target;
		if (!isTable($this))
			return;
		const caretPos = doGetCaretPosition($this);
		const childSelector = e.ctrlKey ? 'tbody' : 'tr';
		const containerEl = $this.closest(childSelector);
		const inputArray = Array.from(containerEl.querySelectorAll('td'));

		const inputs = getFocusableInputs(inputArray);
		if (e.key === 'Home' || e.keyCode === 36) {
			// Allow home key functionality to work inside of the input
			if (caretPos === 0 || e.ctrlKey) {
				e.preventDefault();
				inputs[0].focus();
			}
		}
		else if (e.key === 'End' || e.keyCode === 35) {
			// Allow end key functionality to work inside of the input
			if (caretPos === $this.value.length || e.ctrlKey) {
				e.preventDefault();
				inputs.slice(-1)[0].focus();
			}
		}
	});

	/*
		Returns the caret (cursor) position of the specified text field.
		http://stackoverflow.com/questions/2897155/get-cursor-position-in-characters-within-a-text-input-field
	*/
	const doGetCaretPosition = field => {
		let iCaretPos = 0;
		if (field.selectionStart || field.selectionStart === '0')
			iCaretPos = field.selectionStart;
		// Return results
		return (iCaretPos);
	};

	// Navigate table cells using arrow keys
	addHandler(focusableElements, 'keydown', (e) => {
		const $this = e.target;
		if (!isTable($this))
			return;
		// keyCode numbers needed for IE11 support
		const keys = {
			'37': { x: -1, y: 0 },	// ArrowLeft
			'38': { x: 0, y: -1 },	// ArrowUp
			'39': { x: 1, y: 0 },	// ArrowRight
			'40': { x: 0, y: 1 }	// ArrowDown
		};

		const key = keys[e.keyCode]; // keyCode is needed for IE11 support

		if (key) {
			// do not override moving cursor when editing input/textarea
			const caretPos = doGetCaretPosition($this);
			if (!$this.matches('.cog-picker input')) {
				if ($this.matches('input[type=text], input[type=email], input[type=tel], input[type=url], textarea') && !(key.x === 0 || (key.x < 0 && caretPos === 0) || (key.x > 0 && caretPos === $this.value.length)))
					return;
				else if ($this.matches('textarea') && !(key.y === 0 || (key.y < 0 && caretPos === 0) || (key.y > 0 && caretPos === $this.value.length)))
					return;
			}

			e.stopImmediatePropagation();
			e.preventDefault();

			// Row inputs
			const currentRow = $this.closest('tr');
			const rowTdElementArray = Array.from(currentRow.querySelectorAll('td'));
			const focusableInputsInRow = getFocusableInputs(rowTdElementArray);
			const focusedInputIndexInRow = focusableInputsInRow.indexOf($this);

			// Column inputs
			const rows = Array.from($this.closest('tbody').children);
			const column = rows.map(row => getFocusableInputs(Array.from(row.children))[focusedInputIndexInRow]);
			const colCell = rows.indexOf(currentRow);

			if (key.x) {
				// Find the next focusable input for navigation in the row
				const targetInput = focusableInputsInRow[Math.max(0, focusedInputIndexInRow + key.x)];
				if ((Math.max(0, focusedInputIndexInRow + key.x)) <= (focusableInputsInRow.length - 1))
					targetInput.focus();
			}
			else {
				// Find the next focusable input for navigation in the column
				const targetInput = column[Math.max(0, colCell + key.y)];
				if ((Math.max(0, colCell + key.y)) <= (column.length - 1))
					targetInput.focus();
			}
		}
	});
}
